import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { backofficeLinks } from "@/components/lib/SidebarLinks";
import { useState, useEffect } from "react";
import Stepper from "@/components/common/stepper";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import Step1 from "@/components/menu-management/create-your-own-general/add-create-your-own-general/step1";
import Step2 from "@/components/menu-management/create-your-own-general/add-create-your-own-general/step2";
import Step3 from "@/components/menu-management/create-your-own-general/add-create-your-own-general/step3";
import useAuth from "@/hooks/useAuth";
import CreateMenuSkeleton from "@/components/skeleton/create-menu-skeleton";
import { setItemVariants } from "@/redux/slice/essential-menu-slice/variants-slice";
import StepFormButtons from "@/components/common/step-for-button";
import { getFirstErrorMessage } from "@/lib/utils";
import {
  createNewCreateYourOwnGeneral,
  updateExistingCreateYourOwnGeneral,
} from "@/redux/slice/menu-manage-slice/create-your-own-general-slice";
import { fetchAllFlatMenu } from "@/redux/slice/menu-manage-slice/menu-slice";
import { fetchAllDays } from "@/redux/slice/utility/day-slice";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useStepFormHandler } from "@/hooks/useStepFormHandler";

const normalizeDayIds = (items) => {
  if (!Array.isArray(items)) return [];
  return items
    .map((dt) =>
      typeof dt === "object" && dt !== null ? dt.day_id ?? dt.id : dt
    )
    .filter((id) => id !== null && id !== undefined && !isNaN(Number(id)))
    .map(Number);
};

function CreateCreateYourOwnGeneral() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { type } = useParams();
  const location = useLocation();
  const stepLabels = ["Step 1", "Step 2", "Step 3"];
  const { user } = useAuth();
  const [files, setFiles] = useState([]);
  const item = location.state;
  const [dayTimes, setDayTimes] = useState([]);

  const { createLoading, updateLoading } = useSelector(
    (state) => state.menuManage.createYourOwnGeneral
  );
  const { getLoading } = useSelector((state) => state.menuManage.menu);
  const { getLoading: daysLoading } = useSelector((state) => state.utility);

  const form = useForm({
    resolver: zodResolver(createYourOwnGeneralSchema),
    mode: "onChange",
    defaultValues: {
      type: "create",
      item_no: null,
      name: "",
      description: "",
      person_limit: null,
      days: [],
      courses: [],
      price: 0,
      vat: 0,
      status: "active",
      file_manager_id: undefined,
    },
  });

  const errorMessage = getFirstErrorMessage(form);

  useEffect(() => {
    if (user?.restaurant_id) {
      dispatch(fetchAllFlatMenu(user.restaurant_id));
      dispatch(fetchAllDays());
    }
  }, [dispatch, user?.restaurant_id]);

  useEffect(() => {
    const dayIds = normalizeDayIds(dayTimes);
    form.setValue("days", dayIds);
  }, [dayTimes, form]);

  useEffect(() => {
    if (type !== "update") return;
    if (!item) {
      navigate("/backoffice/menu/create-your-own-general");
      return;
    }
    const initialDays = normalizeDayIds(item?.days);
    form.reset({
      type: "update",
      item_no: item?.item_no || null,
      name: item?.name || "",
      description: item?.description || "",
      price: item?.price || null,
      person_limit: item?.person_limit,
      vat: item?.vat ?? 0,
      status: item?.status || "active",
      file_manager_id: item?.file_manager_id || undefined,
      days: initialDays,
      courses: item?.courses || [],
    });

    if (item?.file_manager_id && item?.file_manager_url != null) {
      setFiles([
        {
          file_manager_id: item?.file_manager_id,
          preview: item?.file_manager_url,
          name: item?.name,
        },
      ]);
    }
    setDayTimes(initialDays);
    dispatch(setItemVariants(item?.courses || []));
  }, [type, item, form, navigate, dispatch]);

  const { step, nextStep, setStep, prevStep, entityId, loading } =
    useStepFormHandler({
      type,
      entity: "Choose Your Own",
      user,
      createAction: createNewCreateYourOwnGeneral,
      updateAction: updateExistingCreateYourOwnGeneral,
      totalSteps: stepLabels.length,
      item: item,
      toastSteps: { 0: false, 1: false, 2: true },
      successRedirectLink: "/backoffice/menu/create-your-own-general",
    });

  if (getLoading || daysLoading) return <CreateMenuSkeleton />;

  return (
    <Layout2
      pageName={{
        title: `${
          type === "update"
            ? "Update Choose Your Own"
            : "Add New Choose Your Own"
        }`,
      }}
      paths={[
        { name: "home", path: "/home" },
        { name: "Back Office", path: "/backoffice/overview" },
        { name: "Menu Management", path: "/backoffice/menu/drinks" },
        {
          name: "Choose Your Own",
          path: "/backoffice/menu/create-your-own-general",
        },
        {
          name: `${
            type === "update"
              ? "Update Choose Your Own"
              : "Add New Choose Your Own"
          }`,
          path: `/backoffice/menu/create-your-own-general/${type}`,
        },
      ]}
      LeftBar={<LeftBar2 sidebarLinks={backofficeLinks} />}
    >
      <div className="py-5 container mt-5">
        <Stepper
          steps={stepLabels}
          onStepChange={setStep}
          type={form.watch("type")}
          currentStep={step}
        />
        {errorMessage && (
          <div className="text-cred mb-2 text-sm mt-4 flex justify-center items-center text-center">
            {errorMessage}
          </div>
        )}
        <Form {...form}>
          <form>
            {step === 0 && (
              <Step1
                dayTimes={dayTimes}
                setDayTimes={setDayTimes}
                form={form}
              />
            )}
            {step === 1 && (
              <Step2
                item_id={entityId || item?.id}
                form={form}
              />
            )}
            {step === 2 && (
              <Step3 files={files} setFiles={setFiles} form={form} />
            )}

            <StepFormButtons
              step={step}
              totalSteps={stepLabels.length}
              isLoading={createLoading || updateLoading || loading}
              type={type}
              status={form.watch("status")}
              cancelLink="/backoffice/menu/create-your-own-general"
              onBack={prevStep}
              onNext={async () => {
                const dayIds = normalizeDayIds(dayTimes);
                const formData = {
                  ...form.getValues(),
                  days: dayIds,
                };
                await nextStep("draft", formData, form);
              }}
              onSubmit={async () => {
                const data = form.getValues();
                const dayIds = normalizeDayIds(dayTimes);
                await nextStep(data?.status, { ...data, days: dayIds }, form);
              }}
              onDraftSave={async () => {
                const data = form.getValues();
                const dayIds = normalizeDayIds(dayTimes);
                await nextStep(
                  "draft",
                  { ...data, days: dayIds },
                  form,
                  { draftButton: true }
                );
              }}
            />
          </form>
        </Form>
      </div>
    </Layout2>
  );
}

export default CreateCreateYourOwnGeneral;

export const createYourOwnGeneralSchema = z.object({
  item_no: z
    .preprocess(
      (val) => (val === "" || val == null ? null : Number(val)),
      z
        .number({
          invalid_type_error: "Item no must be a number",
        })
        .nullable()
    )
    .optional(),

  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),

  person_limit: z
    .preprocess(
      (val) => (val === "" || val == null ? null : Number(val)),
      z
        .number({
          invalid_type_error: "Person limit must be a number",
        })
        .nullable()
    )
    .optional(),

  courses: z.array(z.object({}).catchall(z.any())).optional(),

  price: z.preprocess(
    (val) => (val === "" || val == null ? undefined : Number(val)),
    z
      .number({
        required_error: "Price is required",
        invalid_type_error: "Price must be a number",
      })
      .optional()
  ),

  vat: z
    .preprocess(
      (val) => (val === "" || val == null ? 0 : Number(val)),
      z.number({
        invalid_type_error: "VAT must be a number",
      })
    )
    .refine((val) => val >= 0, { message: "VAT cannot be negative" })
    .refine((val) => val <= 100, { message: "VAT cannot exceed 100" })
    .optional(),

  status: z.enum(["active", "inactive", "draft", "archived"]).default("active"),

  file_manager_id: z
    .preprocess(
      (val) => (val === "" || val == null ? undefined : Number(val)),
      z
        .number({
          required_error: "Please upload an image",
          invalid_type_error: "File ID must be a number",
        })
        .min(1, "File ID must be a positive integer")
    )
    .optional(),
});

